import { hWebDb, IOnifyTranslation } from '@/plugins/dexie';
import { Pinia } from 'pinia';
import { groupBy, merge } from 'lodash-es';
import { useHFetch } from './http/use-http-request';
import { load } from 'js-yaml';
import systemTranslationsYAML from '@/shared/translations/strings.yml?raw';
import { useHLocale } from './use-translate';
import { resolveLegacyCustomerStrings } from './legacy-customer-config';
import { resolveActiveAppClientCode } from './use-settings';
import { getBootstrappedPublicStringsState } from './use-public-strings';
import { stripTranslationMeta } from '@/utils/strip-translation-meta';
import { isPublicAuthPath } from '@/utils/public-auth-path';
import { effectScope, reactive, watch } from 'vue';

const CACHE_NAME = 'translations';
interface ITranslationsByLocale {
  [locale: string]: {
    [key: string]: string;
  };
}

interface ITranslationsFromConfig {
  [key: string]: {
    [locale: string]: string;
  };
}

type HelixHttpClient = ReturnType<typeof useHFetch>;

export const translations: ITranslationsByLocale = reactive({});
const systemTranslations = stripTranslationMeta((load(systemTranslationsYAML) as Record<string, unknown>) ?? {}) as ITranslationsFromConfig;
let lastConfigTranslations: ITranslationsFromConfig | undefined;

function getTranslationCacheKeyByLocale(locale: string) {
  return `${CACHE_NAME}_${locale}`;
}

function getKeyTranslationPair(strings: IOnifyTranslation[] = []) {
  return strings.reduce((prev, current) => ({ ...prev, ...(current && { [current.key]: current.translation }) }), {});
}

function applyBundledTranslations(configTranslations?: ITranslationsFromConfig) {
  const cleanedConfig = configTranslations
    ? (stripTranslationMeta(configTranslations as Record<string, unknown>) as ITranslationsFromConfig)
    : ({} as ITranslationsFromConfig);
  const _localTranslations = Object.entries(Object.assign({}, systemTranslations, cleanedConfig)).reduce<ITranslationsByLocale>(
    (prevLocal, [key, localeTranslations]) => {
      const translationsToMerge: ITranslationsByLocale = Object.entries(localeTranslations).reduce(
        (prev, [locale, translation]) => ({
          ...prev,
          [locale]: {
            [key]: translation,
          },
        }),
        {},
      );

      return merge(prevLocal, translationsToMerge);
    },
    {},
  );

  Object.assign(translations, _localTranslations);
}

/**
 * Re-applies bundled YAML + anonymous public strings after IndexedDB/runtime
 * cache clear. Logout must not leave `$t()` as raw keys on the logout/login surfaces.
 */
export function reseedTranslationsAfterCacheClear() {
  applyBundledTranslations(lastConfigTranslations);

  const { locale, strings } = getBootstrappedPublicStringsState();
  if (locale && strings && Object.keys(strings).length) {
    applyPublicStrings(locale, strings);
  }
}

/**
 * A method that loads and updates translations.
 *
 * @function
 * @name loadAllTranslations
 * @description
 * This utility function loads all translations from the database and optionally merges them with provided configuration translations.
 * It updates the global translations object with the loaded and merged translations.
 *
 * @param {ITranslationsFromConfig} [configTranslations] - Optional translations from configuration to merge with system translations.
 * @returns {Promise<void>} - A promise that resolves when all translations are loaded and merged.
 */
export async function loadAllTranslations(configTranslations?: ITranslationsFromConfig) {
  const allStrings = await hWebDb.translations.toArray();

  if (configTranslations) {
    lastConfigTranslations = configTranslations;
    applyBundledTranslations(configTranslations);
  }

  const stringsByLocale = groupBy(allStrings, (str) => str.locale);

  for (const [key, value] of Object.entries(stringsByLocale)) {
    translations[key] = { ...translations[key], ...getKeyTranslationPair(value) };
  }
}

/**
 * A method that loads translations for a given locale.
 *
 * @function
 * @name loadTranslations
 * @description
 * This function fetches translations from the database for the specified locale and updates the translations object.
 *
 * @param {string} locale - The locale for which translations are to be loaded.
 * @returns {Promise<void>} - A promise that resolves when the translations are successfully loaded and updated.
 */
export async function loadTranslations(locale: string) {
  const strings = await hWebDb.translations.where('locale').equals(locale).toArray();
  translations[locale] = { ...translations[locale], ...getKeyTranslationPair(strings) };
}

/**
 * Merges bundled legacy per-customer strings into the runtime translation store —
 * BACKWARD COMPATIBILITY ONLY (see legacy-customer-config.ts).
 *
 * `clientCode` must come from the API's `app_client_code` — never guessed at
 * boot. A client code with no bundled legacy strings (every new, fully API-driven
 * customer) is a correct no-op here.
 *
 * Ordering requirement: callers MUST call this AFTER `loadAllTranslations()` (so it
 * overrides the generic `config/strings.yml` bundle) and BEFORE `applyPublicStrings`
 * / `applyBootstrappedPublicStrings` (so the API always has the final word).
 * This function does not — and cannot — enforce that ordering itself; it always
 * overwrites whatever the target locale/key currently holds.
 */
export function applyLegacyCustomerStrings(clientCode: unknown) {
  const legacyStrings = stripTranslationMeta(resolveLegacyCustomerStrings(clientCode) as Record<string, unknown>);

  for (const [key, localeTranslations] of Object.entries(legacyStrings)) {
    for (const [locale, translation] of Object.entries(localeTranslations)) {
      translations[locale] = {
        ...translations[locale],
        [key]: translation,
      };
    }
  }
}

/**
 * Re-applies bundled legacy per-customer strings whenever the API client code
 * becomes available or changes. Public/authenticated API strings are re-applied
 * afterwards so the API keeps the final word.
 *
 * Initial boot still runs the explicit sequence in main.vue (generic bundle →
 * settings resolve → legacy → public). This watcher covers later refreshes.
 */
export function watchLegacyCustomerStrings(): () => void {
  const scope = effectScope(true);
  scope.run(() => {
    watch(
      () => resolveActiveAppClientCode(),
      (clientCode) => {
        if (!clientCode) {
          return;
        }

        applyLegacyCustomerStrings(clientCode);

        const { locale, strings } = getBootstrappedPublicStringsState();
        if (locale && Object.keys(strings).length) {
          applyPublicStrings(locale, strings);
        }
      },
    );
  });

  return () => scope.stop();
}

/**
 * Merges anonymous public strings into the runtime translation store.
 * Intended to run after bundled defaults are loaded so API values overlay YAML fallbacks.
 */
export function applyPublicStrings(locale: string, strings: Record<string, string> = {}) {
  if (!locale || !strings || typeof strings !== 'object') {
    return;
  }

  translations[locale] = {
    ...translations[locale],
    ...strings,
  };
}

async function cacheStrings(locale: string, strings: IOnifyTranslation[] = [], timestamp?: string) {
  // Delete all strings of specified `locale`
  await hWebDb.translations.where('locale').equals(locale).delete();

  // Add new strings
  await hWebDb.translations.bulkAdd(strings);

  const cacheKey = getTranslationCacheKeyByLocale(locale);

  if (timestamp) {
    await hWebDb.cacheKeys.put({ key: timestamp, name: cacheKey }, cacheKey);
    return;
  }

  console.warn(
    `[settings] Hub omitted strings last_modifieddate for locale "${locale}"; cached without timestamp (will revalidate next boot).`,
  );
}

async function fetchStrings(locale: string, stringsUrl: string, httpClient: HelixHttpClient) {
  const newStringsFromApi =
    (await (await httpClient({ url: `${stringsUrl}`, params: { locale } }).response())?.json<Record<string, string>>()) ?? {};
  return Object.entries(newStringsFromApi).map<IOnifyTranslation>(([key, value]) => ({
    key,
    locale,
    translation: value,
  }));
}

async function hasCachedStringsForLocale(locale: string): Promise<boolean> {
  try {
    const count = await hWebDb.translations.where('locale').equals(locale).count();
    return count > 0;
  } catch (error) {
    console.warn('[settings] Failed to read translations cache:', error);
    return false;
  }
}

async function updateTranslations(locale: string, stringsUrl: string, httpClient: HelixHttpClient) {
  const lastCachedDateString = await hWebDb.cacheKeys.get(getTranslationCacheKeyByLocale(locale));

  const res = await httpClient({ url: `${stringsUrl}/_timestamp` }, { throwHttpErrors: false }).response();

  if (!res || res.status === 204 || res.ok === false) {
    await loadTranslations(locale);
    return;
  }

  const { last_modifieddate } = (await res?.json<{ last_modifieddate: string }>()) ?? {};

  let isCacheInvalid = true;

  if (last_modifieddate) {
    isCacheInvalid = lastCachedDateString?.key !== last_modifieddate;
  } else if (!lastCachedDateString?.key) {
    console.warn(`[settings] Hub omitted strings last_modifieddate for locale "${locale}"; fetching full payload.`);
  }

  // Logout clears Dexie translations but a leftover timestamp must not skip the
  // next login fetch — empty locale cache is always a miss (mirrors settings ensureLoaded).
  if (!isCacheInvalid && !(await hasCachedStringsForLocale(locale))) {
    isCacheInvalid = true;
  }

  if (isCacheInvalid) {
    const newStrings = await fetchStrings(locale, stringsUrl, httpClient);
    await cacheStrings(locale, newStrings, last_modifieddate || undefined);
  }

  // if `max_modifieddate` returns null, something went wrong so we get from cache
  await loadTranslations(locale);
}

/**
 * A Vue composable that updates translations based on user locale settings.
 *
 * Seeds locale from public-strings bootstrap when unset, then watches with
 * `{ immediate: true }` so authenticated `/my/strings` loads for every user —
 * including those with no stored preference.
 */
export function useHUpdateTranslations(pinia?: Pinia, stringsUrl = '/my/strings') {
  const { locale, hSetLocale } = useHLocale();
  const bootLocale = getBootstrappedPublicStringsState().locale;

  if (!locale.value && bootLocale) {
    hSetLocale(bootLocale);
  }

  // Login / logout / error must keep the anonymous public strings. Authenticated
  // `/my/strings` loads after a full reload of a signed-in route.
  if (isPublicAuthPath()) {
    return;
  }

  const httpClient = useHFetch();

  watch(
    locale,
    async () => {
      if (locale.value) {
        await updateTranslations(locale.value, stringsUrl, httpClient);
      }
    },
    { immediate: true },
  );
}

export { applyPublicStrings as applyAuthenticatedStrings };
